import { useState } from 'react';
import { Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import type { Task, Question, Category } from '../../App';
import type { Vendor } from './VendorsScreen';
import type { MicrosoftAccount } from './PINCodesScreen';

interface TaskFormProps {
  task?: Task;
  categories: Category[];
  vendors: Vendor[];
  microsoftAccounts: MicrosoftAccount[];
  defaultCategoryId?: string;
  onSave: (task: Task) => void;
  onCancel: () => void;
}

const QUESTION_TYPES = [
  { value: 'multiple-choice', label: 'Multiple Choice' },
  { value: 'yes-no', label: 'Yes/No' },
  { value: 'number', label: 'Number Field' },
  { value: 'text', label: 'Text Input' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
];

export function TaskForm({ task, categories, vendors, microsoftAccounts, defaultCategoryId, onSave, onCancel }: TaskFormProps) {
  const [taskTitle, setTaskTitle] = useState(task?.title || '');
  const [categoryId, setCategoryId] = useState(task?.categoryId || defaultCategoryId || '');
  const [questions, setQuestions] = useState<Question[]>(task?.questions || []);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentQuestionType, setCurrentQuestionType] = useState<Question['type']>('text');
  const [currentOptions, setCurrentOptions] = useState<string[]>(['']);
  const [currentRequired, setCurrentRequired] = useState(true);
  const [currentConditionalOn, setCurrentConditionalOn] = useState('');
  const [currentConditionalValue, setCurrentConditionalValue] = useState('');
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);

  // Role assignment state
  const [assignToOperators, setAssignToOperators] = useState(task?.assignedTo?.operators || false);
  const [assignedVendorIds, setAssignedVendorIds] = useState<string[]>(task?.assignedTo?.vendors || []);
  const [assignedSupervisorIds, setAssignedSupervisorIds] = useState<string[]>(() => {
    const sup = task?.assignedTo?.supervisors;
    if (!sup) return [];
    if (sup === true) return microsoftAccounts.map(s => s.id); // legacy: assign to all
    return Array.isArray(sup) ? sup : [];
  });
  const [activeRoleTab, setActiveRoleTab] = useState<'operators' | 'vendors' | 'supervisors'>('operators');

  const handleAddQuestion = () => {
    if (!currentQuestion.trim()) return;
    if (currentConditionalOn && !currentConditionalValue) {
      alert('Please choose which answer should trigger this follow-up question');
      return;
    }

    const newQuestion: Question = {
      id: editingQuestionId || `q-${Date.now()}`,
      type: currentQuestionType,
      question: currentQuestion,
      options: ['multiple-choice', 'dropdown', 'checkbox'].includes(currentQuestionType) 
        ? currentOptions.filter(opt => opt.trim() !== '') 
        : undefined,
      required: currentRequired,
      conditionalOn: currentConditionalOn
        ? { questionId: currentConditionalOn, value: currentConditionalValue }
        : undefined,
    };

    if (editingQuestionId) {
      setQuestions(questions.map(q => q.id === editingQuestionId ? newQuestion : q));
      setEditingQuestionId(null);
    } else {
      setQuestions([...questions, newQuestion]);
    }

    // Reset form
    setCurrentQuestion('');
    setCurrentQuestionType('text');
    setCurrentOptions(['']);
    setCurrentRequired(true);
    setCurrentConditionalOn('');
    setCurrentConditionalValue('');
  };

  const handleEditQuestion = (question: Question) => {
    setCurrentQuestion(question.question);
    setCurrentQuestionType(question.type);
    setCurrentOptions(question.options || ['']);
    setCurrentRequired(question.required !== false);
    setCurrentConditionalOn(question.conditionalOn?.questionId || '');
    setCurrentConditionalValue(question.conditionalOn?.value || '');
    setEditingQuestionId(question.id);
  };

  const handleCancelEdit = () => {
    setCurrentQuestion('');
    setCurrentQuestionType('text');
    setCurrentOptions(['']);
    setCurrentRequired(true);
    setCurrentConditionalOn('');
    setCurrentConditionalValue('');
    setEditingQuestionId(null);
  };

  const handleDeleteQuestion = (questionId: string) => {
    setQuestions(prev =>
      prev
        .filter(q => q.id !== questionId)
        .map(q => {
          if (q.conditionalOn?.questionId === questionId) {
            const { conditionalOn, ...rest } = q;
            return rest;
          }
          return q;
        })
    );

    if (editingQuestionId === questionId) {
      handleCancelEdit();
    } else if (currentConditionalOn === questionId) {
      setCurrentConditionalOn('');
      setCurrentConditionalValue('');
    }
  };

  const handleMoveQuestion = (index: number, direction: 'up' | 'down') => {
    const newQuestions = [...questions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newQuestions.length) return;
    
    [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
    setQuestions(newQuestions);
  };

  const handleSaveTask = () => {
    if (!taskTitle.trim()) {
      alert('Please enter a task title');
      return;
    }

    if (!categoryId) {
      alert('Please select a category');
      return;
    }

    if (questions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    // Validate that at least one role is assigned
    if (!assignToOperators && assignedSupervisorIds.length === 0 && assignedVendorIds.length === 0) {
      alert('Please assign this task to at least one role (Operators, Vendors, or Supervisors)');
      return;
    }

    const category = categories.find(c => c.id === categoryId);
    if (!category) {
      alert('Invalid category selected');
      return;
    }

    const newTask: Task = {
      id: task?.id || `task-${Date.now()}`,
      title: taskTitle.trim(),
      category: 'mechanical', // Keep for backwards compatibility but not displayed
      categoryId: categoryId,
      questions: questions,
      assignedTo: {
        operators: assignToOperators,
        supervisors: assignedSupervisorIds,
        vendors: assignedVendorIds,
      },
    };

    onSave(newTask);
  };

  const addOptionField = () => {
    setCurrentOptions([...currentOptions, '']);
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...currentOptions];
    newOptions[index] = value;
    setCurrentOptions(newOptions);
  };

  const removeOption = (index: number) => {
    if (currentOptions.length > 1) {
      setCurrentOptions(currentOptions.filter((_, i) => i !== index));
    }
  };

  const requiresOptions = ['multiple-choice', 'dropdown', 'checkbox'].includes(currentQuestionType);
  const availableParentQuestions = questions.filter(q => {
    if (editingQuestionId && q.id === editingQuestionId) return false;
    return ['yes-no', 'multiple-choice', 'dropdown'].includes(q.type);
  });

  const conditionalOptions = (() => {
    if (!currentConditionalOn) return [];
    const parentQuestion = questions.find(q => q.id === currentConditionalOn);
    if (!parentQuestion) return [];

    if (parentQuestion.type === 'yes-no') {
      return ['Yes', 'No'];
    }

    return parentQuestion.options || [];
  })();

  const getQuestionPositionLabel = (questionId: string) => {
    const questionIndex = questions.findIndex(q => q.id === questionId);
    return questionIndex >= 0 ? `Q${questionIndex + 1}` : 'Selected question';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">
          {task ? 'Edit Task' : 'Create New Task'}
        </h3>

        {/* Task Details */}
        <div className="space-y-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Task Title *
            </label>
            <input
              type="text"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              placeholder="Enter task title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Role Assignment Section */}
        <div className="border-t border-gray-200 pt-6 pb-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Assign Task To *</h4>
          <p className="text-sm text-gray-600 mb-4">Select who can access and complete this task</p>
          
          {/* Horizontal Tab Navigation */}
          <div className="flex gap-2 border-b border-gray-200 mb-4">
            <button
              onClick={() => setActiveRoleTab('operators')}
              className={`px-4 py-2 font-medium text-sm transition ${
                activeRoleTab === 'operators'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Gama Operators
            </button>
            <button
              onClick={() => setActiveRoleTab('vendors')}
              className={`px-4 py-2 font-medium text-sm transition ${
                activeRoleTab === 'vendors'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Vendors {assignedVendorIds.length > 0 && <span className="ml-1 text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">{assignedVendorIds.length}</span>}
            </button>
            <button
              onClick={() => setActiveRoleTab('supervisors')}
              className={`px-4 py-2 font-medium text-sm transition ${
                activeRoleTab === 'supervisors'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Supervisors {assignedSupervisorIds.length > 0 && <span className="ml-1 text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">{assignedSupervisorIds.length}</span>}
            </button>
          </div>

          {/* Tab Content with Scrollbar */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            {activeRoleTab === 'operators' && (
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={assignToOperators}
                    onChange={(e) => setAssignToOperators(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Assign to all Gama Operators</span>
                </label>
              </div>
            )}

            {activeRoleTab === 'vendors' && (
              <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                {vendors.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No vendors available. Create vendors first.</p>
                ) : (
                  vendors.map(vendor => (
                    <label key={vendor.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded transition">
                      <input
                        type="checkbox"
                        checked={assignedVendorIds.includes(vendor.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssignedVendorIds([...assignedVendorIds, vendor.id]);
                          } else {
                            setAssignedVendorIds(assignedVendorIds.filter(id => id !== vendor.id));
                          }
                        }}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">{vendor.name}</span>
                    </label>
                  ))
                )}
              </div>
            )}

            {activeRoleTab === 'supervisors' && (
              <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                {microsoftAccounts.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No supervisors available. Add supervisor accounts in Manage Access first.</p>
                ) : (
                  microsoftAccounts.map(account => (
                    <label key={account.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded transition">
                      <input
                        type="checkbox"
                        checked={assignedSupervisorIds.includes(account.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssignedSupervisorIds([...assignedSupervisorIds, account.id]);
                          } else {
                            setAssignedSupervisorIds(assignedSupervisorIds.filter(id => id !== account.id));
                          }
                        }}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <div>
                        <span className="text-sm text-gray-700">{account.name || account.email}</span>
                        {account.name && <span className="text-xs text-gray-400 ml-1">({account.email})</span>}
                      </div>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Questions Section */}
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Questions</h4>

          {/* Add/Edit Question Form */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4 mb-6">
            <h5 className="font-semibold text-gray-900">
              {editingQuestionId ? 'Edit Question' : 'Add New Question'}
            </h5>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Text *
              </label>
              <input
                type="text"
                value={currentQuestion}
                onChange={(e) => setCurrentQuestion(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                placeholder="Enter your question"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Type *
                </label>
                <select
                  value={currentQuestionType}
                  onChange={(e) => {
                    setCurrentQuestionType(e.target.value as Question['type']);
                    setCurrentOptions(['']);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                >
                  {QUESTION_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={currentRequired}
                    onChange={(e) => setCurrentRequired(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Required field</span>
                </label>
              </div>
            </div>

            {/* Options for multiple choice, dropdown, checkbox */}
            {requiresOptions && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Options *
                </label>
                <div className="space-y-2">
                  {currentOptions.map((option, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                        placeholder={`Option ${index + 1}`}
                      />
                      {currentOptions.length > 1 && (
                        <button
                          onClick={() => removeOption(index)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={addOptionField}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    + Add Option
                  </button>
                </div>
              </div>
            )}

            {availableParentQuestions.length > 0 && (
              <div className="border-t border-blue-200 pt-4">
                <h6 className="font-medium text-gray-900 mb-3">Follow-up Question</h6>
                <p className="text-sm text-gray-600 mb-3">
                  Make this a follow-up question that only appears after a specific answer.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Show this question when
                    </label>
                    <select
                      value={currentConditionalOn}
                      onChange={(e) => {
                        setCurrentConditionalOn(e.target.value);
                        setCurrentConditionalValue('');
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-white"
                    >
                      <option value="">Always show this question</option>
                      {availableParentQuestions.map((question, index) => (
                        <option key={question.id} value={question.id}>
                          Q{index + 1}: {question.question}
                        </option>
                      ))}
                    </select>
                  </div>

                  {currentConditionalOn && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Required answer
                      </label>
                      <select
                        value={currentConditionalValue}
                        onChange={(e) => setCurrentConditionalValue(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-white"
                      >
                        <option value="">Select answer</option>
                        {conditionalOptions.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleAddQuestion}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
              >
                <Save className="w-4 h-4" />
                {editingQuestionId ? 'Update Question' : 'Add Question'}
              </button>
              {editingQuestionId && (
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </div>

          {/* Existing Questions */}
          {questions.length > 0 && (
            <div className="space-y-3">
              <h5 className="font-semibold text-gray-900">Current Questions ({questions.length})</h5>
              {questions.map((question, index) => (
                <div
                  key={question.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col gap-1 pt-1">
                      <button
                        onClick={() => handleMoveQuestion(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => handleMoveQuestion(index, 'down')}
                        disabled={index === questions.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{question.question}</p>
                          <div className="flex gap-3 mt-2">
                            <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded">
                              {QUESTION_TYPES.find(t => t.value === question.type)?.label}
                            </span>
                            {question.required !== false && (
                              <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                                Required
                              </span>
                            )}
                          </div>
                          {question.options && question.options.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-600 mb-1">Options:</p>
                              <ul className="text-sm text-gray-700 list-disc list-inside">
                                {question.options.map((opt, i) => (
                                  <li key={i}>{opt}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {question.conditionalOn && (
                            <div className="mt-2">
                              <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded">
                                Follow-up: shows when {getQuestionPositionLabel(question.conditionalOn.questionId)} = "{question.conditionalOn.value}"
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleEditQuestion(question)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Task Button */}
        <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handleSaveTask}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold"
          >
            {task ? 'Update Task' : 'Create Task'}
          </button>
          <button
            onClick={onCancel}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-semibold"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
